<script setup lang="ts">
  import { computed, type HTMLAttributes } from 'vue';
  import type { SwitchRootEmits, SwitchRootProps } from 'reka-ui';
  import { reactiveOmit } from '@vueuse/core';
  import { SwitchRoot, SwitchThumb, useForwardPropsEmits } from 'reka-ui';
  import { cn } from '@/shared/lib/utils';

  /**
   * `size` only changes the track and the travel of the thumb. `default` keeps the
   * measurements every caller already had; `lg` is the 36 by 20 track of the design
   * system, with the thumb inset by 2 on both ends.
   */
  const props = defineProps<
    SwitchRootProps & { class?: HTMLAttributes['class']; size?: 'default' | 'lg' }
  >();

  const emits = defineEmits<SwitchRootEmits>();

  const delegatedProps = reactiveOmit(props, 'class', 'size');

  const forwarded = useForwardPropsEmits(delegatedProps, emits);

  const isLarge = computed(() => props.size === 'lg');

  const trackClass = computed(() => (isLarge.value ? 'h-5 w-9 border-2' : 'h-[1.15rem] w-8 border'));

  const thumbTravelClass = computed(() =>
    isLarge.value
      ? 'data-[state=checked]:translate-x-4'
      : 'data-[state=checked]:translate-x-[calc(100%-2px)]'
  );
</script>

<template>
  <SwitchRoot
    data-slot="switch"
    v-bind="forwarded"
    :class="
      cn(
        'peer switch-root inline-flex shrink-0 items-center rounded-full border-transparent shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
        trackClass,
        props.class
      )
    "
    :style="{
      '--switch-checked-bg': 'var(--theme-secondary)',
      '--switch-unchecked-bg': 'var(--color-gray-02)',
      '--switch-ring-color': 'var(--theme-secondary)',
    }"
  >
    <SwitchThumb
      data-slot="switch-thumb"
      :class="
        cn(
          'switch-thumb pointer-events-none block size-4 rounded-full ring-0 transition-transform data-[state=unchecked]:translate-x-0',
          thumbTravelClass
        )
      "
 class="bg-white-01"
    >
      <slot name="thumb" />
    </SwitchThumb>
  </SwitchRoot>
</template>

<style scoped>
  .switch-root[data-state='checked'] {
    background-color: var(--switch-checked-bg) !important;
  }

  .switch-root[data-state='unchecked'] {
    background-color: var(--switch-unchecked-bg) !important;
  }

  .switch-root:focus-visible {
    --tw-ring-color: var(--switch-ring-color);
  }
</style>
